import React, { useMemo, useEffect, useState } from "react"

import { Container, CardBody, Card } from "reactstrap"
import Breadcrumb from "components/Common/Breadcrumb"
import { useLocation, useNavigate } from "react-router-dom"

import moment from "moment"
import { axiosbaseurl } from "configure"
import Loader from "components/Common/Loader"
import { usePermissionData } from "AppContext"
import PermittedComponent from "components/Common/PermittedComponent"
import TimesheetFilter from "components/Common/TimesheetFilter"
import TimeHistory from "pages/Timesheet/TimeHistory"
import CommonTable from "components/Common/CommonTable"

const ArchiveTasks = () => {
  const [table_data, settable_data] = useState([])
  const [all_tasks, setAllTasks] = useState([])
  const [all_services, setAllServices] = useState([])
  const [status_options, setstatus_options] = useState([])
  const [emp_options, setemp_options] = useState([])

  const [loading, setLoading] = useState(false)

  const locationData = useLocation()

  useEffect(() => {
    const getTasks = async () => {
      try {
        setLoading(true)
        const response = await axiosbaseurl.get("/tasks/archived")
        if (response.data.success) {
          const tempTasks = response.data.data
            .map(item => ({
              ...item,
              clientName: item?.client?.clientName,
              service_name: item?.serviceName?.serviceName,
              statusName: item?.status?.statusName,
              periodEnd: item?.startDate,
              timer: item.timer || false,
              assistantName: `${item?.assistant?.name || "-"} ${
                item?.assistant?.lastName || "-"
              }`,
            }))
            ?.filter(t => t.client)
            .sort((a, b) => new Date(a.updatedAt) - new Date(b.updatedAt))

          if (locationData.state?.data)
            settable_data(
              tempTasks.filter(
                task => task?.status?.id == locationData.state?.data
              )
            )
          else settable_data(tempTasks)

          setAllTasks(tempTasks)

          // creating employees options
          setEmployeesOptions(response.data.data)

          // making service oprtions
          const services = tempTasks?.map(t => t.serviceName)
          const uniqueServices = services?.filter(
            (service, index, self) =>
              index === self?.findIndex(s => s.id === service.id)
          )
          setAllServices(uniqueServices)

          // making status oprtions
          const status = tempTasks?.map(t => t.status)
          const uniqueStatus = status?.filter(
            (sts, index, self) =>
              index === self?.findIndex(s => s.id === sts.id)
          )
          setstatus_options(uniqueStatus)
        }
      } catch (error) {
        console.log(error)
      } finally {
        setLoading(false)
      }
    }
    getTasks()
  }, [])

  function setEmployeesOptions(employees = []) {
    const emps = []
    const empTypes = [
      "clientManager",
      "productionManager",
      "assistant",
      "administrator",
    ]

    employees.forEach(curr => {
      assistants.forEach(empType => {
        if (!emps.some(i => i?.id === curr[empType]?.id) && curr[empType])
          emps.push({ ...curr[empType], isAssistant: true })
      })
    })

    employees.forEach(curr => {
      empTypes.forEach(empType => {
        if (!emps.some(i => i?.id === curr[empType]?.id) && curr[empType])
          emps.push(curr[empType])
      })
    })

    setemp_options(emps)
  }

  document.title = "Task Management | Peacock Accountants Limited"

  return (
    <>
      {loading && <Loader />}
      <div className="page-content mb-5">
        <Container fluid>
          <Breadcrumb title={"Dashboard"} breadcrumbItem={"Archive Tasks"} />

          <Card className="px-0 mx-0">
            <CardBody>
              <TableComponent
                table_data={table_data}
                settable_data={settable_data}
                all_tasks={all_tasks}
                setAllTasks={setAllTasks}
                all_services={all_services}
                status_options={status_options}
                emp_options={emp_options}
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </>
  )
}

const TableComponent = ({
  table_data,
  settable_data,
  all_tasks,
  setAllTasks,
  all_services,
  status_options,
  emp_options,
}) => {
  const [showHistory, setShowHistory] = useState(false)
  const [taskData, setTaskData] = useState(null)

  const navigate = useNavigate()

  const { checkAccess, permissions } = usePermissionData()
  const module_id = 2

  const revertTask = task_id => {
    swal({
      title: "Are you sure?",
      text: "You really want to revert task.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async willDelete => {
      if (willDelete) {
        try {
          const response = await axiosbaseurl.patch(`/tasks/${task_id}`)
          if (response.data.success) {
            swal("", "Task Reverted Successfully", "success")

            settable_data(prev => prev.filter(task => task.id !== task_id))
            setAllTasks(prev => prev.filter(task => task.id !== task_id))
          } else swal("", "Something went wrong", "error")
        } catch (error) {
          console.log(error)
          swal("", "Something went wrong", "error")
        }
      }
    })
  }

  // columns
  const columns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
        filterable: true,
        disableFilters: true,
        className: " text-center",
        Cell: cellProps => <span>{cellProps.row.index + 1}</span>,
      },
      {
        Header: "Client Code",
        accessor: "client.clientCode",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Client Name",
        accessor: "client.clientName",
        disableFilters: true,
        filterable: true,
        Cell: cellProps => (
          <span>{cellProps.row.original.client.clientName}</span>
        ),
      },
      {
        Header: "Task Name",
        accessor: "title",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Period End",
        accessor: "startDate",
        disableFilters: true,
        filterable: true,
        // className: "text-center",
        Cell: cellProps => (
          <span>
            {moment.utc(cellProps.row.original.startDate).format("DD MMM YYYY")}
          </span>
        ),
      },
      {
        Header: "Due Date",
        accessor: "dueDate",
        disableFilters: true,
        filterable: true,
        // className: "text-center",
        Cell: cellProps => (
          <span>
            {moment.utc(cellProps.row.original.dueDate).format("DD MMM YYYY")}
          </span>
        ),
      },
      {
        Header: "Assistant",
        accessor: "assistantName",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Status",
        disableSort: true,
        accessor: "statusName",
        disableFilters: true,
        filterable: true,
        className: "text-center",
        Cell: cellProps => {
          const { colorCode, statusName } = cellProps.row.original?.status
          return (
            <div className="text-center">
              <span
                className="badge"
                style={{
                  color: colorCode,
                  border: `1px solid ${colorCode}`,
                  fontSize: "11px",
                }}
              >
                {statusName}{" "}
              </span>
            </div>
          )
        },
      },
      {
        Header: "Action",
        disableFilters: true,
        disableSort: true,
        accessor: "view",
        className: `text-center col-2`,
        Cell: cellProps => {
          const { id, clientId, title, client } = cellProps.row.original

          return (
            <div>
              <button
                onClick={() => revertTask(id)}
                className="btn btn-danger btn-sm ml-10"
              >
                <i className="dripicons-enter me-1"></i> Revert
              </button>

              <button
                onClick={() => {
                  setShowHistory(true)
                  setTaskData({
                    task: id,
                    client: clientId,
                    taskName: title,
                    clientName: client?.clientName,
                  })
                }}
                title="View Work History"
                className="btn btn-dark btn-sm ms-2"
              >
                <i className="fas fa-history me-1"></i> Logs
              </button>
            </div>
          )
        },
      },
    ],
    [permissions, table_data]
  )

  // table data
  const data = useMemo(() => table_data, [table_data])

  return (
    <>
      <PermittedComponent isAccess={checkAccess(module_id, "view")}>
        <CommonTable columns={columns} data={data} heading={"Archive Tasks"}>
          <div>
            {/* <TimesheetFilter
                            data={{
                                status_options,
                                table_data,
                                settable_data,
                                all_tasks,
                                all_services,
                                emp_options,
                                type: "task"
                            }}
                        /> */}

            <button
              onClick={() => navigate(-1)}
              className="btn btn-secondary bg-dark ms-3"
            >
              <i className="fas fa-arrow-left me-3"></i>
              Back
            </button>
          </div>
        </CommonTable>

        <TimeHistory
          show={showHistory}
          setShow={setShowHistory}
          taskData={taskData}
          setTaskData={setTaskData}
          settable_data={settable_data}
        />
      </PermittedComponent>
    </>
  )
}

export default ArchiveTasks
